import { IAlbum } from '@/data/models/album';
import type {
  ICurrentAlbumSalesTeamChanges,
  ICurrentChangeField,
  ICurrentChanges,
  ICurrentPricingChangesGroup,
  ICurrentProductMetadataChangesGroup,
  ICurrentTerritoriesChangesGroup,
  ICurrentTrackChanges,
  ICurrentTracksChangesGroup,
} from '@/types/product-current-changes-types';
import { IAlbumSalesTeamPrice } from '@/data/models/salesTerritory';
import { ITrack } from '@/data/models/track';
import _ from 'lodash';

type AlbumMetaData = NonNullable<IAlbum['albumMetaData']>;
type Manufacturing = NonNullable<AlbumMetaData['manufacturing']>;
type Marketing = NonNullable<AlbumMetaData['marketing']>;
type SalesProjection = NonNullable<AlbumMetaData['salesProjection']>;
type CurrentChangesContainer = {
  changesCounter: number;
};
type ChangeFieldDefinition<TSource> = {
  key: string;
  getValue: (source?: TSource) => unknown;
};
type SingleCountFieldPair = [string, string];

// Change configuration:
// - Add new fields to these key arrays when an existing category grows.
// - For a new category, reuse buildFlatChanges or buildListChanges below.
const albumChangeFields: Array<keyof IAlbum> = [
  'title',
  'format',
  'artists',
  'catalogNumber',
  'releaseDate',
  'originalReleaseDate',
  'packaging',
  'notes',
];

const albumMetaDataChangeFields: Array<keyof AlbumMetaData> = [
  'genre',
  'pricing',
  'unitsPerSets',
  'boxCount',
  'retailCoopBudget',
  'coopPlannerTargetAccounts',
  'topTenTargetMarkets',
  'comments',
  'metadataLanguage',
  'copyrightYear',
  'copyrightLine',
  'phonographicCopyrightYear',
  'phonographicCopyrightLine',
  'versions',
  'packagingDescription',
  'internalDescription',
  'releaseType',
  'videoContentType',
];

const manufacturingFieldKeys: Array<keyof Manufacturing> = [
  'isManufacturedByIngrooves',
  'manufacturerName',
  'explicitLyrics',
  'ingroovesCanadaRight',
  'countryOfManufacture',
];

const marketingFieldKeys: Array<keyof Marketing> = [
  'marketingInformation',
  'projectOverview',
  'tourInfo',
  'radioVideoSetup',
];

const salesProjectionFieldKeys: Array<keyof SalesProjection> = [
  'weekOneProjection',
  'weekOneDigital',
  'weekOneDigitalSalesProjection',
  'weekOnePhysicialSalesProjection',
  'projectedPhysicalIO',
];

const trackFieldKeys: Array<keyof ITrack> = [
  'trackNumber',
  'title',
  'version',
  'discOrSideNumber',
  'artists',
  'writersComposers',
  'publishers',
  'duration',
  'copyrightYear',
  'copyrightLine',
  'phonographicCopyrightYear',
  'phonographicCopyrightLine',
  'genre',
  'audioLanguage',
  'trackType',
];

const territoryFieldKeys: Array<keyof AlbumMetaData> = [
  'territoryRestrictions',
  'territoryRestrictionScheme',
];

const pricingFieldKeys: Array<keyof IAlbumSalesTeamPrice> = [
  'price',
  'isPrimary',
];

const singleCountFieldPairs: SingleCountFieldPair[] = [
  ['copyrightYear', 'copyrightLine'],
  ['phonographicCopyrightYear', 'phonographicCopyrightLine'],
];

// Converts a top-level album field into a reusable comparison definition.
const albumField = <TKey extends keyof IAlbum>(
  field: TKey
): ChangeFieldDefinition<IAlbum> => ({
  key: String(field),
  getValue: (album) => album?.[field],
});

// Converts an albumMetaData field into a reusable comparison definition.
const albumMetaDataField = <TKey extends keyof AlbumMetaData>(
  field: TKey
): ChangeFieldDefinition<IAlbum> => ({
  key: String(field),
  getValue: (album) => album?.albumMetaData?.[field],
});

// Converts a manufacturing field into a product metadata comparison definition.
const manufacturingField = <TKey extends keyof Manufacturing>(
  field: TKey
): ChangeFieldDefinition<IAlbum> => ({
  key: String(field),
  getValue: (album) => album?.albumMetaData?.manufacturing?.[field],
});

// Converts a marketing field into a product metadata comparison definition.
const marketingField = <TKey extends keyof Marketing>(
  field: TKey
): ChangeFieldDefinition<IAlbum> => ({
  key: String(field),
  getValue: (album) => album?.albumMetaData?.marketing?.[field],
});

// Converts a salesProjection field into a product metadata comparison definition.
const salesProjectionField = <TKey extends keyof SalesProjection>(
  field: TKey
): ChangeFieldDefinition<IAlbum> => ({
  key: String(field),
  getValue: (album) => album?.albumMetaData?.salesProjection?.[field],
});

// Groups currency and cost because the edit UI treats them as one field.
const currencyManufacturingCostPerUnitField: ChangeFieldDefinition<IAlbum> = {
  key: 'currencyManufacturingCostPerUnit',
  getValue: (album) => ({
    currency: album?.albumMetaData?.manufacturing?.currency,
    manufacturingCostPerUnit:
      album?.albumMetaData?.manufacturing?.manufacturingCostPerUnit,
  }),
};

// Groups account exclusive fields because the edit UI treats them as one field.
const accountExclusiveField: ChangeFieldDefinition<IAlbum> = {
  key: 'accountExclusive',
  getValue: (album) => ({
    exclusiveReleaseAccount: album?.albumMetaData?.exclusiveReleaseAccount,
    exclusiveReleaseAccountName:
      album?.albumMetaData?.exclusiveReleaseAccountName,
  }),
};

// Groups preorder date and time because the edit UI treats them as one field.
const retailPreOrderField: ChangeFieldDefinition<IAlbum> = {
  key: 'retailPreOrder',
  getValue: (album) =>
    [
      album?.albumMetaData?.retailPreOrderDate,
      album?.albumMetaData?.retailPreOrderTime,
    ]
      .filter(Boolean)
      .join(' '),
};

// Converts a direct item field into a reusable comparison definition.
const itemField = <TSource, TKey extends keyof TSource>(
  field: TKey
): ChangeFieldDefinition<TSource> => ({
  key: String(field),
  getValue: (source) => source?.[field],
});

const productMetadataFields: Array<ChangeFieldDefinition<IAlbum>> = [
  ...albumChangeFields.map(albumField),
  ...albumMetaDataChangeFields.map(albumMetaDataField),
  ...manufacturingFieldKeys.map(manufacturingField),
  ...marketingFieldKeys.map(marketingField),
  ...salesProjectionFieldKeys.map(salesProjectionField),
  currencyManufacturingCostPerUnitField,
  accountExclusiveField,
  retailPreOrderField,
];

const trackFields: Array<ChangeFieldDefinition<ITrack>> = trackFieldKeys.map(
  (field) => itemField<ITrack, keyof ITrack>(field)
);

const territoryFields: Array<ChangeFieldDefinition<IAlbum>> =
  territoryFieldKeys.map(albumMetaDataField);

const pricingFields: Array<ChangeFieldDefinition<IAlbumSalesTeamPrice>> =
  pricingFieldKeys.map((field) =>
    itemField<IAlbumSalesTeamPrice, keyof IAlbumSalesTeamPrice>(field)
  );

// Normalizes values before comparison so whitespace and object key order do not create false changes.
const normalizeChangeValue = (value: unknown): unknown => {
  if (_.isNil(value)) {
    return null;
  }

  if (_.isString(value)) {
    return value.trim();
  }

  if (_.isDate(value)) {
    return value.toISOString();
  }

  if (_.isArray(value)) {
    return value.map(normalizeChangeValue);
  }

  if (_.isPlainObject(value)) {
    return _(value as Record<string, unknown>)
      .mapValues(normalizeChangeValue)
      .toPairs()
      .sortBy(([key]) => key)
      .fromPairs()
      .value();
  }

  return value;
};

// Creates the base shape for flat change groups.
const createChangesGroup = <TGroup extends CurrentChangesContainer>() =>
  ({
    changesCounter: 0,
  }) as TGroup;

// Adds one field comparison, stores the original value, and updates the counter.
const addCurrentChange = (
  changesGroup: CurrentChangesContainer,
  key: string,
  originalValue: unknown,
  changeRequestValue: unknown
) => {
  const hasChanges = !_.isEqual(
    normalizeChangeValue(originalValue),
    normalizeChangeValue(changeRequestValue)
  );

  (changesGroup as unknown as Record<string, ICurrentChangeField | number>)[
    key
  ] = {
    hasChanges,
    value: _.cloneDeep(originalValue),
  };

  if (hasChanges) {
    changesGroup.changesCounter += 1;
  }
};

// Adds a configured list of field comparisons to the target group.
const addFieldChanges = <TSource>(
  changesGroup: CurrentChangesContainer,
  originalSource: TSource | undefined,
  changeRequestSource: TSource | undefined,
  fields: Array<ChangeFieldDefinition<TSource>>
) => {
  fields.forEach(({ key, getValue }) => {
    addCurrentChange(
      changesGroup,
      key,
      getValue(originalSource),
      getValue(changeRequestSource)
    );
  });
};

// Keeps paired fields available for the UI, but counts each changed pair only once.
const compactSingleCountFieldPairs = (
  changesGroup: CurrentChangesContainer
) => {
  const changeFields = changesGroup as unknown as Record<
    string,
    ICurrentChangeField | number
  >;

  singleCountFieldPairs.forEach(([firstField, secondField]) => {
    const firstChange = changeFields[firstField] as
      | ICurrentChangeField
      | undefined;
    const secondChange = changeFields[secondField] as
      | ICurrentChangeField
      | undefined;

    if (firstChange?.hasChanges && secondChange?.hasChanges) {
      changesGroup.changesCounter = Math.max(
        changesGroup.changesCounter - 1,
        0
      );
    }
  });
};

// Builds flat groups such as product metadata and territory restrictions.
const buildFlatChanges = <TSource, TGroup extends CurrentChangesContainer>(
  originalSource: TSource | undefined,
  changeRequestSource: TSource | undefined,
  fields: Array<ChangeFieldDefinition<TSource>>
): TGroup => {
  const changesGroup = createChangesGroup<TGroup>();

  addFieldChanges(changesGroup, originalSource, changeRequestSource, fields);
  compactSingleCountFieldPairs(changesGroup);

  return changesGroup;
};

// Builds list groups such as tracks and album sales teams.
const buildListChanges = <
  TSource,
  TItemChanges extends CurrentChangesContainer,
  TGroup extends { changesCounter: number },
>({
  originalItems,
  changeRequestItems,
  fields,
  createGroup,
  createItemChanges,
  addItemChanges,
}: {
  originalItems: TSource[];
  changeRequestItems: TSource[];
  fields: Array<ChangeFieldDefinition<TSource>>;
  createGroup: () => TGroup;
  createItemChanges: (
    originalItem?: TSource,
    changeRequestItem?: TSource
  ) => TItemChanges;
  addItemChanges: (group: TGroup, itemChanges: TItemChanges) => void;
}): TGroup => {
  const changesGroup = createGroup();
  const itemCount =
    _.max([originalItems.length, changeRequestItems.length]) ?? 0;

  _.times(itemCount, (index) => {
    const originalItem = originalItems[index];
    const changeRequestItem = changeRequestItems[index];
    const itemChanges = createItemChanges(originalItem, changeRequestItem);

    addFieldChanges(itemChanges, originalItem, changeRequestItem, fields);
    compactSingleCountFieldPairs(itemChanges);

    changesGroup.changesCounter += itemChanges.changesCounter;
    addItemChanges(changesGroup, itemChanges);
  });

  return changesGroup;
};

// Builds product metadata changes from the productMetadataFields configuration.
const buildProductMetadataChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentProductMetadataChangesGroup =>
  buildFlatChanges<IAlbum, ICurrentProductMetadataChangesGroup>(
    originalAlbum,
    changeRequestAlbum,
    productMetadataFields
  );

// Creates the base shape for track changes.
const createTracksChangesGroup = (): ICurrentTracksChangesGroup => ({
  changesCounter: 0,
  tracks: [],
});

// Creates one track item and keeps ids available for future filtering.
const createTrackChanges = (
  originalTrack?: ITrack,
  changeRequestTrack?: ITrack
): ICurrentTrackChanges =>
  ({
    id: changeRequestTrack?.id ?? originalTrack?.id,
    albumId: changeRequestTrack?.albumId ?? originalTrack?.albumId,
    changesCounter: 0,
  }) as ICurrentTrackChanges;

// Builds track changes from the trackFields configuration.
const buildTracksChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentTracksChangesGroup =>
  buildListChanges({
    originalItems: originalAlbum.tracks ?? [],
    changeRequestItems: changeRequestAlbum.tracks ?? [],
    fields: trackFields,
    createGroup: createTracksChangesGroup,
    createItemChanges: createTrackChanges,
    addItemChanges: (group, itemChanges) => {
      group.tracks.push(itemChanges);
    },
  });

// Builds territory changes from the territoryFields configuration.
const buildTerritoriesChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentTerritoriesChangesGroup => {
  const territoriesChanges = buildFlatChanges<
    IAlbum,
    ICurrentTerritoriesChangesGroup
  >(originalAlbum, changeRequestAlbum, territoryFields);

  // Territory restrictions and scheme are edited together, so they count as one change.
  territoriesChanges.changesCounter =
    territoriesChanges.changesCounter > 0 ? 1 : 0;

  return territoriesChanges;
};

// Creates the base shape for pricing changes.
const createPricingChangesGroup = (): ICurrentPricingChangesGroup => ({
  changesCounter: 0,
  albumSalesTeams: [],
});

// Creates one pricing item and keeps ids available for future filtering.
const createPricingChanges = (
  originalPrice?: IAlbumSalesTeamPrice,
  changeRequestPrice?: IAlbumSalesTeamPrice
): ICurrentAlbumSalesTeamChanges =>
  ({
    id: changeRequestPrice?.id ?? originalPrice?.id,
    albumId: changeRequestPrice?.albumId ?? originalPrice?.albumId,
    salesTeamId: changeRequestPrice?.salesTeamId ?? originalPrice?.salesTeamId,
    changesCounter: 0,
  }) as ICurrentAlbumSalesTeamChanges;

// Builds pricing changes from the pricingFields configuration.
const buildPricingChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentPricingChangesGroup => {
  const originalItemsBySalesTeamId = _.keyBy(
    originalAlbum.albumSalesTeams ?? [],
    'salesTeamId'
  );
  const changeRequestItemsBySalesTeamId = _.keyBy(
    changeRequestAlbum.albumSalesTeams ?? [],
    'salesTeamId'
  );
  const salesTeamIds = _.uniq([
    ...Object.keys(originalItemsBySalesTeamId),
    ...Object.keys(changeRequestItemsBySalesTeamId),
  ]);

  const pricingChanges = createPricingChangesGroup();

  salesTeamIds.forEach((salesTeamId) => {
    const originalPrice = originalItemsBySalesTeamId[salesTeamId];
    const changeRequestPrice = changeRequestItemsBySalesTeamId[salesTeamId];
    const itemChanges = createPricingChanges(originalPrice, changeRequestPrice);

    addFieldChanges(
      itemChanges,
      originalPrice,
      changeRequestPrice,
      pricingFields
    );

    pricingChanges.changesCounter += itemChanges.changesCounter;
    pricingChanges.albumSalesTeams.push(itemChanges);
  });

  pricingChanges.changesCounter = pricingChanges.changesCounter > 0 ? 1 : 0;

  return pricingChanges;
};

// Sums each category counter into the global change counter.
const getTotalChangesCounter = (
  changeGroups: Array<{ changesCounter: number }>
) =>
  changeGroups.reduce(
    (totalChangesCounter, changeGroup) =>
      totalChangesCounter + changeGroup.changesCounter,
    0
  );

// Builds the full currentChanges object from each category builder.
export const productCurrentChanges = (
  originalAlbum: IAlbum,
  changeRequestAlbum: IAlbum
): ICurrentChanges => {
  // Step 1: Build each category independently of its field configuration.
  const productMetadataChanges = buildProductMetadataChanges(
    originalAlbum,
    changeRequestAlbum
  );
  const tracksChanges = buildTracksChanges(originalAlbum, changeRequestAlbum);
  const territoriesChanges = buildTerritoriesChanges(
    originalAlbum,
    changeRequestAlbum
  );
  const pricingChanges = buildPricingChanges(originalAlbum, changeRequestAlbum);

  // Step 2: Add the category counters to expose one total count.
  const totalChangesCounter = getTotalChangesCounter([
    productMetadataChanges,
    tracksChanges,
    territoriesChanges,
    pricingChanges,
  ]);

  // Step 3: Return the shape consumed by the view layer.
  return {
    totalChangesCounter,
    productMetadataChanges,
    tracksChanges,
    territoriesChanges,
    pricingChanges,
  };
};
